import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { Check, Hourglass, X } from '@phosphor-icons/react'
import { cn } from '@/lib/cn'
import '../board.css'

/**
 * The V1 console boards' kit (alignment map F4-09, F4-B4.a/b/c): page head, counter tiles, a plain
 * table with a selected row, status pills with their round icon, small tags and the 300px side
 * panel with the brown edge. Pages that use it sit inside <BoardPage> (data-surface="board").
 */
export function BoardPage({ title, actions, children }: { title: ReactNode; actions?: ReactNode; children: ReactNode }) {
  return (
    <div data-surface="board" className="flex flex-col gap-4 text-ink">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-[26px] font-bold leading-tight">{title}</h1>
        {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
      </div>
      {children}
    </div>
  )
}

/** Main column + the 300px side panel; stacks below lg. */
export function BoardColumns({ main, side }: { main: ReactNode; side?: ReactNode }) {
  return (
    <div className="grid grid-cols-1 items-start gap-[18px] lg:grid-cols-[minmax(0,1fr)_300px]">
      <div className="flex min-w-0 flex-col gap-4">{main}</div>
      {side}
    </div>
  )
}

export function Counters({ children }: { children: ReactNode }) {
  return <div className="grid grid-cols-2 gap-3 md:grid-cols-4">{children}</div>
}

export function Counter({
  label,
  value,
  to,
  onClick,
  active,
  loading,
}: {
  label: ReactNode
  value: ReactNode
  to?: string
  onClick?: () => void
  active?: boolean
  loading?: boolean
}) {
  const cls = cn(
    'flex flex-col gap-1 rounded-card border-[1.5px] bg-surface px-4 py-3 text-left',
    active ? 'border-amber' : 'border-rule',
    (to || onClick) && 'transition-colors hover:border-amber focus-visible:border-amber focus-visible:outline-none',
  )
  const body = (
    <>
      <span className="text-[14px] text-muted">{label}</span>
      <b className={cn('text-[26px] leading-none tabular', loading && 'animate-pulse text-muted')}>{loading ? '…' : value}</b>
    </>
  )
  if (to)
    return (
      <Link to={to} className={cls}>
        {body}
      </Link>
    )
  if (onClick)
    return (
      <button type="button" className={cls} aria-pressed={active} onClick={onClick}>
        {body}
      </button>
    )
  return <div className={cls}>{body}</div>
}

export interface BoardColumn<T> {
  id: string
  header: ReactNode
  cell: (row: T) => ReactNode
  align?: 'left' | 'right'
  width?: string
  /** Hide on narrow screens. */
  hideBelow?: 'sm' | 'md'
}

const hideCls = { sm: 'hidden sm:table-cell', md: 'hidden md:table-cell' } as const

/** The boards' table: white card, dark rule under the header, the selected row in sunk brown. */
export function BoardTable<T>({
  caption,
  columns,
  rows,
  getRowId,
  selectedId,
  onRowClick,
  loading,
  error,
  empty,
  pager,
}: {
  caption: string
  columns: ReadonlyArray<BoardColumn<T>>
  rows: ReadonlyArray<T>
  getRowId: (row: T) => string
  selectedId?: string | null
  onRowClick?: (row: T) => void
  loading?: boolean
  error?: ReactNode
  empty?: ReactNode
  pager?: { page: number; total: number; limit: number; onPage: (page: number) => void }
}) {
  const pages = pager ? Math.max(1, Math.ceil(pager.total / pager.limit)) : 1
  return (
    <div className="overflow-hidden rounded-card border-[1.5px] border-rule bg-surface">
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <caption className="sr-only">{caption}</caption>
          <thead>
            <tr>
              {columns.map((c) => (
                <th
                  key={c.id}
                  scope="col"
                  style={c.width ? { width: c.width } : undefined}
                  className={cn(
                    'whitespace-nowrap border-b-[1.5px] border-ink px-2.5 py-[9px] text-[13px] font-bold text-muted',
                    c.align === 'right' ? 'text-right' : 'text-left',
                    c.hideBelow && hideCls[c.hideBelow],
                  )}
                >
                  {c.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && rows.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="px-2.5 py-6 text-center text-[15px] text-muted">
                  Loading…
                </td>
              </tr>
            ) : error ? (
              <tr>
                <td colSpan={columns.length} className="px-2.5 py-6 text-center text-[15px] text-danger">
                  {error}
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="px-2.5 py-6 text-center text-[15px] text-muted">
                  {empty ?? 'Nothing here.'}
                </td>
              </tr>
            ) : (
              rows.map((r) => {
                const id = getRowId(r)
                const on = selectedId === id
                return (
                  <tr
                    key={id}
                    aria-selected={onRowClick ? on : undefined}
                    tabIndex={onRowClick ? 0 : undefined}
                    onClick={onRowClick ? () => onRowClick(r) : undefined}
                    onKeyDown={
                      onRowClick
                        ? (e) => {
                            if (e.target !== e.currentTarget) return
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault()
                              onRowClick(r)
                            }
                          }
                        : undefined
                    }
                    className={cn(
                      on ? 'bg-paper-tint' : 'bg-surface',
                      onRowClick && 'cursor-pointer hover:bg-paper-tint/60 focus-visible:bg-paper-tint/60 focus-visible:outline-none',
                    )}
                  >
                    {columns.map((c) => (
                      <td
                        key={c.id}
                        className={cn(
                          'border-b border-rule px-2.5 py-2.5 align-top text-[15px]',
                          c.align === 'right' && 'whitespace-nowrap text-right tabular',
                          c.hideBelow && hideCls[c.hideBelow],
                        )}
                      >
                        {c.cell(r)}
                      </td>
                    ))}
                  </tr>
                )
              })
            )}
          </tbody>
        </table>
      </div>
      {pager && pages > 1 && (
        <div className="flex items-center justify-end gap-2 px-3 py-2 text-[14px] text-muted">
          <button type="button" className="rounded-chip border border-rule px-2 py-0.5 disabled:opacity-40" disabled={pager.page <= 1} onClick={() => pager.onPage(pager.page - 1)}>
            Previous
          </button>
          <span>
            Page {pager.page} of {pages}
          </span>
          <button type="button" className="rounded-chip border border-rule px-2 py-0.5 disabled:opacity-40" disabled={pager.page >= pages} onClick={() => pager.onPage(pager.page + 1)}>
            Next
          </button>
        </div>
      )}
    </div>
  )
}

export type PillTone = 'wait' | 'bad' | 'ok' | 'idle' | 'help'

/** Status pill: a round icon and bold words (waiting = hourglass, bad = cross, ok = tick, idle = dot). */
export function Pill({ tone, children, className, wrap }: { tone: PillTone; children: ReactNode; className?: string; wrap?: boolean }) {
  const dot = 'inline-flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full'
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 self-start rounded-[16px] py-[3px] pl-1 pr-2.5 text-[14px] font-bold leading-tight',
        wrap ? 'whitespace-normal' : 'whitespace-nowrap',
        tone === 'wait' && 'bg-paper-tint text-ink',
        tone === 'bad' && 'bg-danger-wash text-danger',
        tone === 'ok' && 'bg-ok-wash text-ok',
        tone === 'idle' && 'bg-surface text-muted',
        tone === 'help' && 'bg-info-wash text-info',
        className,
      )}
    >
      {tone === 'wait' && (
        <span aria-hidden="true" className={cn(dot, 'border-[1.5px] border-[#8a827a] bg-paper-tint text-ink')}>
          <Hourglass size={13} weight="bold" />
        </span>
      )}
      {tone === 'bad' && (
        <span aria-hidden="true" className={cn(dot, 'bg-danger text-white')}>
          <X size={13} weight="bold" />
        </span>
      )}
      {tone === 'ok' && (
        <span aria-hidden="true" className={cn(dot, 'bg-ok text-white')}>
          <Check size={13} weight="bold" />
        </span>
      )}
      {tone === 'idle' && (
        <span aria-hidden="true" className={cn(dot, 'border-2 border-[#8a827a] text-[13px] font-bold text-[#8a827a]')}>
          ·
        </span>
      )}
      {tone === 'help' && (
        <span aria-hidden="true" className={cn(dot, 'bg-info text-white text-[13px] font-bold')}>
          i
        </span>
      )}
      {children}
    </span>
  )
}

export type TagTone = 'good' | 'paper' | 'bad' | 'plain' | 'help'

/** Small tag next to a lot code (Good, Good · paper, Red tie, Lot payment, Top-up, About the account). */
export function Tag({ tone, children }: { tone: TagTone; children: ReactNode }) {
  return (
    <span
      className={cn(
        'inline-flex w-fit items-center whitespace-nowrap rounded-chip px-2 py-px text-[13px] font-bold',
        tone === 'good' && 'bg-ok-wash text-ok',
        tone === 'paper' && 'bg-amber-wash text-amber-dark',
        tone === 'bad' && 'bg-danger-wash text-danger',
        tone === 'plain' && 'bg-paper-tint text-ink',
        tone === 'help' && 'bg-info-wash text-info',
      )}
    >
      {children}
    </span>
  )
}

/** Bold line + muted line (lot code / farmer + village). */
export function TwoLine({ top, sub, children }: { top: ReactNode; sub?: ReactNode; children?: ReactNode }) {
  return (
    <span className="flex flex-col items-start gap-0.5 leading-tight">
      <b>{top}</b>
      {sub && <span className="text-[14px] text-muted">{sub}</span>}
      {children}
    </span>
  )
}

/** The right-hand panel with the boards' brown edge. */
export function SidePanel({ title, sub, children, label }: { title: ReactNode; sub?: ReactNode; children: ReactNode; label?: string }) {
  return (
    <aside aria-label={label} className="flex min-w-0 flex-col gap-2.5 rounded-card border-[1.5px] border-amber bg-surface p-4 lg:sticky lg:top-4">
      <div className="flex flex-col">
        <b className="text-[19px] leading-tight">{title}</b>
        {sub && <span className="text-[14px] text-muted">{sub}</span>}
      </div>
      {children}
    </aside>
  )
}

/** Label left, value right (the panels' fact rows). */
export function FactRow({ label, children, strong }: { label?: ReactNode; children: ReactNode; strong?: boolean }) {
  return (
    <div className={cn('flex items-baseline justify-between gap-3 text-[15px]', strong && 'font-bold')}>
      <span className="shrink-0">{label}</span>
      <span className="min-w-0 text-right tabular">{children}</span>
    </div>
  )
}

export function Facts({ children }: { children: ReactNode }) {
  return <div className="flex flex-col gap-1.5">{children}</div>
}

/** Muted small print. */
export function Note({ children, className }: { children: ReactNode; className?: string }) {
  return <span className={cn('text-[13px] leading-snug text-muted', className)}>{children}</span>
}

/** Push the panel's buttons to the bottom, full width. */
export function PanelActions({ children }: { children: ReactNode }) {
  return <div className="mt-1 flex flex-col gap-2 [&>*]:w-full">{children}</div>
}

/** A ticked line in "done by the system". */
export function Done({ children, done = true }: { children: ReactNode; done?: boolean }) {
  return (
    <div className="flex items-start gap-2">
      <span
        aria-hidden="true"
        className={cn(
          'mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full',
          done ? 'bg-ok text-white' : 'border-2 border-[#8a827a] text-[#8a827a]',
        )}
      >
        {done ? <Check size={12} weight="bold" /> : null}
      </span>
      <span className="text-[14px]">{children}</span>
    </div>
  )
}

/** Board-style link button (white with grey edge, or the brown main action). */
export function LinkButton({ to, children, primary, icon }: { to: string; children: ReactNode; primary?: boolean; icon?: ReactNode }) {
  return (
    <Link
      to={to}
      className={cn(
        'inline-flex h-10 items-center justify-center gap-2 rounded-chip border-[1.5px] px-[14px] text-[15px] font-bold',
        primary ? 'border-amber bg-amber text-white hover:border-amber-dark hover:bg-amber-dark' : 'border-[#8a827a] bg-surface text-ink hover:bg-paper-tint',
      )}
    >
      {icon}
      {children}
    </Link>
  )
}
